import { EmptyState } from '../components/EmptyState.js';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useBrand } from '../lib/brand-context.js';
import { apiGet, apiFetch } from '../lib/api.js';
import { useToast } from '../hooks/useToast.js';
import { Icon } from '../components/ui/Icon.js';
import { Region } from '../components/ui/Region.js';
import { CollectionStatus } from '../components/CollectionStatus.js';
import { CostPreview } from '../components/CostPreview.js';
import { CollectButton } from '../components/CollectButton.js';
import { ExecutionMatrix } from '../components/ExecutionMatrix.js';
import { CollectionHistory } from '../components/CollectionHistory.js';
import { CollectionKpiTiles } from '../components/CollectionKpiTiles.js';
import { EngineStrip } from '../components/EngineStrip.js';
import type {
  CollectionPreviewResponse,
  CollectionPreview,
  CollectionMatrixResponse,
  CollectionMatrixRow,
  CollectionHistoryResponse,
  CollectionRun,
  PipelineStatus,
} from '../types/index.js';

export function CollectionPage() {
  const { selectedBrand, user } = useBrand();
  const { addToast } = useToast();

  const [preview, setPreview] = useState<CollectionPreview | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [matrix, setMatrix] = useState<CollectionMatrixRow[]>([]);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [history, setHistory] = useState<CollectionRun[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [collecting, setCollecting] = useState(false);
  // Spec 256: each engine's own last run, for the engine strip.
  const [lastByEngine, setLastByEngine] = useState<Record<string, string | null> | undefined>(undefined);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Spec 076: tenant id comes from the selected brand, not the user object.
  // `user` is still referenced to gate rendering; we keep the destructure.
  void user;
  const tenantId = selectedBrand?.tenant_id;
  const brandId = selectedBrand?.id;

  // Stop polling when component unmounts
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  const fetchPreview = useCallback(async () => {
    if (!tenantId || !brandId) return;
    setPreviewLoading(true);
    try {
      const res = await apiGet<CollectionPreviewResponse>(`/api/v1/tenants/${tenantId}/collection/preview?brand_id=${brandId}`);
      setPreview(res.data);
      setPreviewFailed(false);
    } catch {
      setPreviewFailed(true);
    } finally {
      setPreviewLoading(false);
    }
  }, [tenantId, brandId]);

  const fetchMatrix = useCallback(async () => {
    if (!tenantId || !brandId) return;
    setMatrixLoading(true);
    try {
      const res = await apiGet<CollectionMatrixResponse>(`/api/v1/tenants/${tenantId}/collection/matrix?brand_id=${brandId}`);
      setMatrix(res.data);
    } catch {
      // silent
    } finally {
      setMatrixLoading(false);
    }
  }, [tenantId, brandId]);

  const fetchHistory = useCallback(async () => {
    if (!tenantId || !brandId) return;
    setHistoryLoading(true);
    try {
      // Spec 124 — brand-scope the history so it reflects the selected brand.
      const res = await apiGet<CollectionHistoryResponse>(`/api/v1/tenants/${tenantId}/collection/history?limit=5&brand_id=${brandId}`);
      setHistory(res.data);
    } catch {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  }, [tenantId, brandId]);

  const fetchLastRuns = useCallback(async () => {
    if (!tenantId || !brandId) return;
    try {
      const res = await apiGet<PipelineStatus>(`/api/v1/tenants/${tenantId}/brands/${brandId}/pipeline/status`);
      setLastByEngine(res.queries.last_by_engine ?? {});
    } catch {
      // Non-critical: the strip leaves out "Last ran" rather than guess.
    }
  }, [tenantId, brandId]);

  useEffect(() => {
    fetchPreview();
    fetchMatrix();
    fetchHistory();
    fetchLastRuns();
  }, [fetchPreview, fetchMatrix, fetchHistory, fetchLastRuns]);

  const startPolling = useCallback(() => {
    // Clear any existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    let pollCount = 0;
    const maxPolls = 60; // 5 minutes at 5-second intervals

    pollingRef.current = setInterval(async () => {
      pollCount++;
      try {
        // Refetch both matrix and history — state updates happen inside these functions
        await Promise.all([fetchMatrix(), fetchHistory(), fetchLastRuns()]);

        // Check completion by querying history directly (avoids stale closure on matrix)
        if (tenantId && brandId) {
          const histRes = await apiFetch(`/api/v1/tenants/${tenantId}/collection/history?limit=1&brand_id=${brandId}`);
          if (histRes.ok) {
            const histData = await histRes.json() as { data: Array<{ jobs_pending: number; jobs_created: number; jobs_completed: number; jobs_running?: number }> };
            const latest = histData.data?.[0];
            // Spec 095: include `jobs_running` so polling doesn't declare
            // "complete" while zombies are still pending recovery on the
            // next sweep tick.
            const latestRunning = latest?.jobs_running ?? 0;
            if (latest && latest.jobs_pending === 0 && latestRunning === 0 && latest.jobs_completed > 0 && pollCount > 2) {
              if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
              setCollecting(false);
              addToast(`Collection complete: ${latest.jobs_completed}/${latest.jobs_created} queries finished`, 'success');
              return;
            }
          }
        }
      } catch {
        // Ignore polling errors
      }

      if (pollCount >= maxPolls) {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        setCollecting(false);
        addToast('Collection polling timed out — jobs may still be processing', 'error');
      }
    }, 5000);
  }, [fetchMatrix, fetchHistory, fetchLastRuns, tenantId, addToast]);

  const handleCollectBrand = async () => {
    if (!selectedBrand) return;
    setCollecting(true);
    try {
      const res = await apiFetch('/api/v1/admin/scheduler/trigger', {
        method: 'POST',
        body: JSON.stringify({ scope: 'brand', brand_id: selectedBrand.id }),
      });
      if (!res.ok) throw new Error('Trigger failed');
      const result = await res.json() as { data: { jobsCreated?: number; jobs_created?: number } };
      const jobsCreated = result.data?.jobsCreated ?? result.data?.jobs_created ?? 0;
      addToast(`Collection started for ${selectedBrand.name}: ${jobsCreated} queries scheduled`, 'success');
      // Refresh data immediately, then start polling for progress
      await Promise.all([fetchMatrix(), fetchHistory()]);
      startPolling();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to trigger collection', 'error');
      setCollecting(false);
    }
  };

  const handleTestPrompt = async (promptTemplateId: string) => {
    if (!selectedBrand) {
      addToast('Select a brand to test a prompt', 'error');
      return;
    }
    try {
      const res = await apiFetch('/api/v1/admin/scheduler/trigger', {
        method: 'POST',
        body: JSON.stringify({
          scope: 'prompt',
          brand_id: selectedBrand.id,
          prompt_template_id: promptTemplateId,
        }),
      });
      if (!res.ok) throw new Error('Test trigger failed');
      const result = await res.json() as { data: { jobsCreated?: number; jobs_created?: number } };
      const jobsCreated = result.data?.jobsCreated ?? result.data?.jobs_created ?? 0;
      addToast(`Test started: ${jobsCreated} queries scheduled`, 'success');
      await fetchMatrix();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to test prompt', 'error');
    }
  };

  if (!user) {
    return null;
  }

  if (!selectedBrand) {
    return (
      <div data-testid="collection-page" className="hub-page--dashboard" data-density="compact">
        <Region>
          <h1 className="hub-page__title">Collection</h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">Monitor and trigger data collection runs</p>
        </Region>
        <Region>
          <EmptyState icon="bx-data" title="Select a brand to manage data collection" description="" />
        </Region>
      </div>
    );
  }

  return (
    <div data-testid="collection-page" className="hub-page--dashboard" data-density="compact">
      {/* Head region: title + the page's one primary action. */}
      <Region>
        <div className="hub-page__head">
          <div>
            <h1 className="hub-page__title">Collection</h1>
            <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">
              Collect data for <span className="font-medium text-[var(--hub-color-ink)]">{selectedBrand.name}</span>
            </p>
          </div>
          <CollectButton
            preview={preview}
            onCollect={handleCollectBrand}
            collecting={collecting}
          />
        </div>

        {/* Spec 256: the engine strip (it absorbed spec 134's degradation
            banner). Health comes with the preview; each engine's own last run
            from the brand's pipeline status. */}
        <div className="mt-4">
          <EngineStrip
            degradation={preview?.degradation}
            lastByEngine={lastByEngine}
            loading={!preview}
            unavailable={!preview && previewFailed}
            onRetry={fetchPreview}
          />
        </div>
      </Region>

      {/* Spec 095/231: the KPI tiles are one instrument now — a StatRow
          (built inside CollectionKpiTiles) rather than three separate
          bordered tiles. Counts come from the brand-level breakdown surfaced
          by /collection/preview. Zeros render while preview is loading
          (avoids flicker — no empty-state ghost). */}
      <Region>
        <CollectionKpiTiles
          eligibleNow={preview?.jobs_eligible_now ?? 0}
          scheduledLater={preview?.jobs_scheduled_later ?? 0}
          recovered24h={preview?.jobs_running_recovered_24h ?? 0}
        />
        <p
          className="mt-3 text-xs text-[var(--hub-color-ink-muted)]"
          data-testid="collection-explainer"
        >
          Jobs are scheduled across 7 days per the platform's cost-management rule — they
          complete progressively, not all at once. Stuck-running jobs are auto-recovered
          every 15 minutes.
        </p>
      </Region>

      {/* Status bar — spec 124: brand-scoped, derived from the brand's history. */}
      <CollectionStatus
        latestRun={history[0] ?? null}
        running={
          !!history[0] &&
          (((history[0].jobs_running ?? 0) > 0) || history[0].jobs_pending > 0)
        }
      />

      {/* Cost preview */}
      <Region>
        <CostPreview preview={preview} loading={previewLoading} brandName={selectedBrand.name} />
      </Region>

      {/* Execution matrix */}
      <Region>
        <ExecutionMatrix
          matrix={matrix}
          onTestPrompt={handleTestPrompt}
          loading={matrixLoading}
        />
      </Region>

      {/* Collection history */}
      <Region>
        <CollectionHistory history={history} loading={historyLoading} onRefresh={() => { fetchMatrix(); fetchHistory(); }} />
      </Region>
    </div>
  );
}
